import { useState, useEffect } from 'react';
import { Archive, MessageSquare, Plus, Send, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Modal } from '../../components/common/Modal';
import { formatDate } from '../../utils/format';

const CATEGORIES = [
  { key: 'idea', label: 'Idea', icon: '💡', color: 'bg-yellow-100 text-yellow-700' },
  { key: 'bug', label: 'Bug', icon: '🐛', color: 'bg-red-100 text-red-700' },
  { key: 'question', label: 'Question', icon: '❓', color: 'bg-blue-100 text-blue-700' },
  { key: 'improvement', label: 'Improvement', icon: '⚡', color: 'bg-purple-100 text-purple-700' },
];

const STATUS_COLORS = {
  open: 'bg-slate-100 text-slate-600',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  archived: 'bg-slate-200 text-slate-500',
};

export const FeedbackModule = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = profile?.role === 'super_admin';

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('feedback').select('*').order('created_at', { ascending: false });
      if (error) { setLoading(false); return; }
      if (data) setItems(data);
      setLoading(false);
    })();
  }, []);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    const { data, error } = await supabase.from('feedback').insert({
      author: profile.email, author_name: profile.name, category, message: message.trim()
    }).select().single();
    if (error) { showToast('Error submitting feedback', 'error'); return; }
    setItems([data, ...items]);
    setMessage('');
    setCategory('idea');
    setShowModal(false);
  };

  const updateStatus = async (id, status) => {
    const { error } = await supabase.from('feedback').update({ status }).eq('id', id);
    if (error) { showToast('Error updating status', 'error'); return; }
    setItems(items.map(i => i.id === id ? { ...i, status } : i));
  };

  const handleArchive = async (id) => {
    const { error } = await supabase.from('feedback').update({ status: 'archived' }).eq('id', id);
    if (error) { showToast('Error archiving feedback', 'error'); return; }
    setItems(items.map(i => i.id === id ? { ...i, status: 'archived' } : i));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    const { error } = await supabase.from('feedback').delete().eq('id', id);
    if (error) { showToast('Error deleting feedback', 'error'); return; }
    setItems(items.filter(i => i.id !== id));
  };

  const handleReply = async (id) => {
    if (!replyText.trim()) return;
    const item = items.find(i => i.id === id);
    const replies = item.replies || [];
    const newReply = { author: profile.name, email: profile.email, role: profile.role, text: replyText.trim(), date: new Date().toISOString() };
    const updatedReplies = [...replies, newReply];
    const { error } = await supabase.from('feedback').update({ replies: updatedReplies }).eq('id', id);
    if (error) { showToast('Error sending reply', 'error'); return; }
    setItems(items.map(i => i.id === id ? { ...i, replies: updatedReplies } : i));
    setReplyText('');
    // Bell notification for the ticket author (skip when replying to yourself).
    if (item.author !== profile.email) {
      const ticket = item.ticket_no != null ? ` #${String(item.ticket_no).padStart(3, '0')}` : '';
      await supabase.from('notifications').insert({ type: 'feedback_reply', message: `Your feedback ticket${ticket} has a new reply — open Team Feedback to read it`, to_email: item.author });
    }
  };

  const isClosed = (s) => s === 'archived' || s === 'resolved';
  // Items in the current view (Active = open/reviewed, or Resolved & Archived).
  const viewItems = showArchived ? items.filter(i => isClosed(i.status)) : items.filter(i => !isClosed(i.status));
  const filtered = filterCategory === 'all' ? viewItems : viewItems.filter(i => i.category === filterCategory);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Team</p>
          <h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">Team Feedback</h2>
          <p className="text-sm text-slate-500">{isAdmin ? 'All team feedback' : 'Submit feedback and track your submissions'}</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm shrink-0">
          <Plus className="w-4 h-4" /> New Feedback
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => { setShowArchived(false); setFilterCategory('all'); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${!showArchived && filterCategory === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>Active ({items.filter(i => !isClosed(i.status)).length})</button>
        {CATEGORIES.map(c => {
          const count = viewItems.filter(i => i.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setFilterCategory(c.key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filterCategory === c.key ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {c.icon} {c.label} ({count})
            </button>
          );
        })}
        <button onClick={() => { setShowArchived(!showArchived); setFilterCategory('all'); }} className={`px-3 py-1.5 rounded-lg text-xs font-medium ml-2 ${showArchived ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}><Archive className="w-3 h-3 inline mr-1" />Resolved & Archived ({items.filter(i => isClosed(i.status)).length})</button>
      </div>

      {/* Feedback list */}
      {loading ? (
        <p className="text-slate-500 text-sm">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center">
          <p className="text-slate-500">No feedback yet. Be the first to share!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(item => {
            const cat = CATEGORIES.find(c => c.key === item.category);
            return (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{cat?.icon}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {item.ticket_no != null && <span className="px-2 py-0.5 text-xs font-bold rounded bg-slate-800 text-white font-mono">#{String(item.ticket_no).padStart(3, '0')}</span>}
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cat?.color}`}>{cat?.label}</span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[item.status]}`}>{item.status}</span>
                          {(item.replies || []).length > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700"><MessageSquare className="w-3 h-3" /> Answered ({item.replies.length})</span>}
                        </div>
                        <p className="text-sm text-slate-800 whitespace-pre-line">{item.message}</p>
                        <p className="text-xs text-slate-400 mt-2">{item.author_name} · {formatDate(item.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      {!isAdmin && item.author === profile?.email && item.status !== 'resolved' && item.status !== 'archived' && (
                        <button onClick={() => updateStatus(item.id, 'resolved')} className="px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-600 hover:bg-green-100">Resolve</button>
                      )}
                      {isAdmin && (
                        <>
                          {item.status === 'open' && <button onClick={() => updateStatus(item.id, 'reviewed')} className="px-2 py-1 text-xs font-medium rounded bg-blue-50 text-blue-600 hover:bg-blue-100">Reviewed</button>}
                          {item.status !== 'resolved' && item.status !== 'archived' && <button onClick={() => updateStatus(item.id, 'resolved')} className="px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-600 hover:bg-green-100">Resolve</button>}
                          {item.status !== 'archived' && <button onClick={() => handleArchive(item.id)} className="px-2 py-1 text-xs font-medium rounded bg-slate-50 text-slate-500 hover:bg-slate-100"><Archive className="w-3 h-3" /></button>}
                          <button onClick={() => handleDelete(item.id)} className="px-2 py-1 text-xs font-medium rounded bg-red-50 text-red-500 hover:bg-red-100"><Trash2 className="w-3 h-3" /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {expandedId === item.id && (
                  <div className="border-t border-slate-100 px-4 pb-4">
                    {/* Replies */}
                    {(item.replies || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {item.replies.map((r, idx) => (
                          <div key={idx} className={`p-3 rounded-lg text-sm ${r.role === 'super_admin' ? 'bg-blue-50 ml-4' : 'bg-slate-50'}`}>
                            <p className="text-slate-800 whitespace-pre-line">{r.text}</p>
                            <p className="text-xs text-slate-400 mt-1">{r.author} · {formatDate(r.date)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Reply input */}
                    <div className="mt-3 flex flex-col sm:flex-row gap-2">
                      <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={3}
                        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleReply(item.id); }}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm resize-y" placeholder="Write a reply... (Ctrl+Enter to send)" />
                      <button onClick={() => handleReply(item.id)} disabled={!replyText.trim()} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New Feedback Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Feedback" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button key={c.key} onClick={() => setCategory(c.key)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border ${category === c.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your idea, bug, question, or suggestion..." />
          </div>
          <button onClick={handleSubmit} disabled={!message.trim()}
            className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
            Submit Feedback
          </button>
        </div>
      </Modal>
    </div>
  );
};
